import { startAuthentication } from '@simplewebauthn/browser';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { RecoveryPending } from '../components/recovery';
import { api, type Recovery } from '../lib/api';
import { errorMessage } from '../lib/format';
import { sessionQuery } from '../lib/session';

/**
 * D11 sign-in: email → 6-digit code → session cookie. Returning users can skip the inbox with a
 * discoverable passkey. First sign-in (no passkey yet) lands on Account with the enrol nudge.
 */
export function LoginPage() {
  const search = useSearch({ from: '/login' });
  const nav = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [otpId, setOtpId] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [mailError, setMailError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<Recovery | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const done = async (hasPasskey: boolean) => {
    // Force a fresh /auth/session read so the route guard sees the new cookie before navigating.
    await qc.fetchQuery({ ...sessionQuery, staleTime: 0 });
    if (!hasPasskey) await nav({ to: '/account', search: { enrol: true } });
    else await nav({ to: search.next ?? '/chat' });
  };

  const start = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.otpStart(email.trim());
      setOtpId(r.otpId);
      setDevCode(r.devCode ?? null);
      setMailError(r.mailError ?? null);
      if (r.devCode) setCode(r.devCode);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (!otpId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.otpVerify(otpId, code.trim());
      if (r.token === null) {
        // passkey holder: the code pulled the recovery escape hatch instead of opening a session
        setRecovery(r.recovery);
        return;
      }
      await done(r.hasPasskey);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const passkey = async () => {
    setBusy(true);
    setError(null);
    try {
      const { challengeId, options } = await api.passkeyLoginOptions();
      const response = await startAuthentication({
        optionsJSON: options as Parameters<typeof startAuthentication>[0]['optionsJSON'],
      });
      await api.passkeyLoginVerify(challengeId, response);
      await done(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="center">
      <div className="panel card stack">
        <h1>Sign in</h1>
        {recovery ? (
          <RecoveryPending
            recovery={recovery}
            email={email}
            onReset={() => {
              setRecovery(null);
              setOtpId(null);
              setCode('');
            }}
          />
        ) : otpId === null ? (
          <form onSubmit={start} className="stack">
            <label className="field">
              <span>Email</span>
              <input
                type="email"
                required
                // biome-ignore lint/a11y/noAutofocus: single-field sign-in step; focus is the only sensible target
                autoFocus
                autoComplete="email webauthn"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </label>
            <button type="submit" className="primary" disabled={busy || !email}>
              Email me a code
            </button>
            <div className="row" style={{ justifyContent: 'center' }}>
              <span className="muted">or</span>
            </div>
            <button type="button" onClick={passkey} disabled={busy}>
              Sign in with a passkey
            </button>
          </form>
        ) : (
          <form onSubmit={verify} className="stack">
            <p className="muted">
              We sent a 6-digit code to <b>{email}</b>. It expires in 10 minutes.
            </p>
            {devCode && (
              <p className="warn">
                Dev mode: the code is <code>{devCode}</code>
                {mailError && <> (email not sent: {mailError})</>}
              </p>
            )}
            <label className="field">
              <span>Code</span>
              <input
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                required
                // biome-ignore lint/a11y/noAutofocus: single-field sign-in step; focus is the only sensible target
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
            <button type="submit" className="primary" disabled={busy || code.length < 6}>
              Verify
            </button>
            <button
              type="button"
              className="small"
              onClick={() => {
                setOtpId(null);
                setCode('');
              }}
            >
              Use a different email
            </button>
          </form>
        )}
        {error && <p className="error">{error}</p>}
      </div>
    </div>
  );
}
